import { useEffect, useState } from 'react';
import { supabase } from '../supabaseClient';
import { readWithTimeout } from '../lib/supabaseRead';

// Lifecycle section (Trade Radar P1, roadmap #3): certificates linked to this
// DA — the "construction is starting / finished" signals. Public council
// record data, visible to all signed-in tiers. Quiet by design: renders
// nothing while loading, on error, or when no certificates are linked.
// Follows the post-incident loading rules: timeout-guarded read, primitive
// deps, stale-write guard, no circuit-breaker pollution.

const CERT_LABELS = {
  'Construction Certificate': { icon: '🏗', label: 'Construction Certificate', hint: 'construction can begin' },
  'Occupation Certificate': { icon: '🔑', label: 'Occupation Certificate', hint: 'build complete / occupiable' },
  'Section 68': { icon: '🔧', label: 'Section 68 Approval', hint: 'services / installations' },
  'Subdivision Certificate': { icon: '📐', label: 'Subdivision Certificate', hint: 'lots registered' },
};

function fmtDate(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
}

export default function DALifecycleSection({ daId, theme }) {
  const [links, setLinks] = useState(null);

  useEffect(() => {
    if (!daId) {
      setLinks(null);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      const { data, error } = await readWithTimeout(
        signal => supabase
          .from('da_lifecycle_links')
          .select('id, cert_type, cert_status, cert_date, confidence')
          .eq('parent_da_id', daId)
          .order('cert_date', { ascending: true })
          .abortSignal(signal),
        { label: 'da_lifecycle_load' }
      );
      if (cancelled) return;
      if (error) {
        console.warn('[DALifecycleSection] load failed:', error.message);
        setLinks(null);
        return;
      }
      setLinks(Array.isArray(data) ? data : []);
    })();
    return () => { cancelled = true; };
  }, [daId]);

  if (!links || links.length === 0) return null;

  return (
    <div
      data-testid="da-lifecycle-section"
      style={{
        marginTop: 14,
        padding: '12px 0 4px',
        borderTop: `1px solid ${theme.border}`,
      }}
    >
      <div style={{ color: theme.textMuted, fontSize: 11, fontWeight: 700, letterSpacing: 0.8, textTransform: 'uppercase', marginBottom: 8 }}>
        Construction Lifecycle
      </div>
      {links.map(link => {
        const meta = CERT_LABELS[link.cert_type] || { icon: '📄', label: link.cert_type, hint: '' };
        const date = fmtDate(link.cert_date);
        return (
          <div key={link.id} style={{ display: 'flex', alignItems: 'baseline', gap: 8, padding: '3px 0', fontSize: 13 }}>
            <span aria-hidden="true">{meta.icon}</span>
            <span style={{ color: theme.text, fontWeight: 600 }}>{meta.label}</span>
            <span style={{ color: theme.textMuted, fontSize: 12 }}>
              {link.cert_status || ''}{date ? ` · ${date}` : ''}
              {link.confidence === 'medium' ? ' · matched by address' : ''}
            </span>
          </div>
        );
      })}
      <div style={{ color: theme.textMuted, fontSize: 11, fontStyle: 'italic', marginTop: 4 }}>
        Linked from council certificate records.
      </div>
    </div>
  );
}
